import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const SLOT_TYPE_TRANSLATABLE_FIELDS = ['label'] as const;
export type SlotTypeTranslatableField = (typeof SLOT_TYPE_TRANSLATABLE_FIELDS)[number];
export type SlotTypeTranslations = TranslatableText<SlotTypeTranslatableField>;

export interface AbsenceTypeBalanceConfig {
  balanceType?: string;
  direction?: 'debit' | 'credit';
}

export const absenceTypeBalanceConfigSchema = type({
  'balanceType?': 'string',
  'direction?': "'debit'|'credit'",
});

export const HOLIDAY_ABSENCE_TYPE_KEY = 'holiday';

/**
 * Slot type categories.
 */
export const slotTypeCategories = ['holiday', 'absence', 'location', 'custom'] as const;
export type SlotTypeCategory = (typeof slotTypeCategories)[number];

const categoryLiteral = "'holiday'|'absence'|'location'|'custom'";

export const slotTypeDataSchema = type({
  'customProperties?': type('Record<string, unknown>').configure({
    description:
      'Custom-property values keyed by the definition `key`. Values are validated against the tenant custom-property definitions for `absence-type` entities.',
    examples: [{ externalCode: 'PAY-042' }],
  }),
}).configure({
  description: 'Extensible slot type data payload.',
  examples: [{ customProperties: { externalCode: 'PAY-042' } }],
});

export type SlotTypeData = typeof slotTypeDataSchema.t;

const slotTypeBehaviorFlagsSchema = type({
  'isBlocking?': 'boolean',
  'isReadOnlyInCalendar?': type('boolean').configure({
    description:
      'Marks kinds owned by another module. Applies when the slot type matches the key or the category (holiday and absence in practice). Slots of such a kind whose `data.source` is not `manual` are read-only: PATCH create or change returns 400.',
  }),
  'countsAgainstBalance?': 'boolean',
  'isHoliday?': 'boolean',
  'carriesWorkBonus?': 'boolean',
  'blocksWork?': 'boolean',
  'supportsRecurrence?': 'boolean',
  'blocksPlanning?': type('boolean').configure({
    description:
      'Absences of this type block shift assignment in the planning solver on their days. Falls back to `isBlocking` when unset.',
  }),
  'requiresApproval?': type('boolean').configure({
    description: 'Tenant approval policy. Absences of this type can be diverted to an approval request instead of a slot row.',
  }),
  'visibleToTeam?': type('boolean').configure({
    description: 'Teammates see the absence type label and colour in the planner. Off shows a neutral marker.',
  }),
  'staffSelectable?': type('boolean').configure({
    description: 'Staff may pick this slot type in the slot editor. `false` reserves it for API-key writers. Absent means `true`.',
  }),
});

export function isSlotTypeStaffSelectable(behaviorFlags: { staffSelectable?: boolean } | null | undefined): boolean {
  return behaviorFlags?.staffSelectable !== false;
}

const PERSON_BOUND_SLOT_TYPES = new Set(['absence', 'holiday', 'work-location', 'location-override']);

export function isPersonBoundSlotType(slotType: string | null | undefined): boolean {
  return !!slotType && PERSON_BOUND_SLOT_TYPES.has(slotType);
}

export const slotTypeDefinitionSchema = type({
  id: 'string',
  key: type('string').configure({
    description: 'Stable identifier, unique per tenant. Equals the slot `data.type` for most kinds.',
    examples: ['vacation'],
  }),
  module: 'string',
  label: type('string').configure({ description: 'Display label resolved for the requested language.' }),
  icon: 'string',
  color: 'string',
  category: type(categoryLiteral),
  behaviorFlags: slotTypeBehaviorFlagsSchema,
  config: type('Record<string, unknown>'),
  priority: 'number',
  isActive: 'boolean',
  isSystem: 'boolean',
  createdAt: 'string',
  updatedAt: 'string',
  data: slotTypeDataSchema.configure({ description: 'Type-level custom-property values live under `data.customProperties`.' }),
  'translations?': type('unknown').as<TranslatableText<string>>().configure({
    description: 'Present when requested with ?include=translations.',
  }),
}).configure({ description: 'Slot type definition (slot kind).' });
export type SlotTypeDefinition = typeof slotTypeDefinitionSchema.infer;

/**
 * Query schema for filtering slot type definitions.
 */
export const slotTypeDefinitionQuerySchema = type({
  'category?': type(categoryLiteral).configure({
    description: "Restrict to one category: 'holiday', 'absence', 'location', or 'custom'.",
    examples: ['absence'],
  }),
  'module?': type('string').configure({
    description: 'Filter by the owning domain module (e.g. the library that registered the type).',
    examples: ['timetracking'],
  }),
  'isActive?': type('boolean').configure({
    description: 'When provided, filters to only active (`true`) or only deactivated (`false`) definitions.',
  }),
}).configure({ description: 'Query parameters for listing slot type definitions.' });
export type SlotTypeDefinitionQuery = typeof slotTypeDefinitionQuerySchema.t;

/**
 * Schema for creating a new slot type definition.
 */
export const createSlotTypeDefinitionSchema = type({
  key: type('string').configure({
    description: 'Stable machine-readable identifier, unique per tenant. Used in slot.data.type.',
    examples: ['vacation'],
  }),
  module: type('string').configure({
    description: 'Owning domain module that registered this definition.',
    examples: ['timetracking'],
  }),
  label: type('string').configure({
    description: 'Human-readable label shown in the UI.',
    examples: ['Vacation'],
  }),
  'icon?': type('string').configure({
    description: 'Material Icons identifier used on slot chips.',
    examples: ['beach_access'],
  }),
  'color?': type('string').configure({
    description: 'CSS color applied to slot chips. Six-digit hex recommended.',
    examples: ['#0ea5e9'],
  }),
  category: type(categoryLiteral).configure({
    description: "Semantic category: 'holiday', 'absence', 'location', or 'custom'.",
    examples: ['absence'],
  }),
  'behaviorFlags?': type('unknown').configure({
    description: 'Feature flags controlling slot behaviour: isBlocking, isReadOnlyInCalendar, countsAgainstBalance, supportsRecurrence.',
  }),
  'config?': type('unknown').configure({
    description: 'Free-form per-type configuration (balance key, approval policy, etc.).',
  }),
  'data?': slotTypeDataSchema,
  'priority?': type('number').configure({
    description: 'Sort order used in pickers. Lower numbers appear first.',
    examples: [10],
  }),
  'isActive?': type('boolean').configure({
    description: 'Whether the definition is offered in the UI. Defaults to `true` when omitted.',
  }),
  'translations?': translatableTextSchema(['label']),
}).configure({ description: 'Payload for creating a new slot type definition.' });
export type CreateSlotTypeDefinition = typeof createSlotTypeDefinitionSchema.t;

/**
 * Schema for updating a slot type definition.
 * System types have restricted editable fields (label, icon, color, isActive, priority).
 */
export const updateSlotTypeDefinitionSchema = type({
  'label?': type('string').configure({ description: 'Updated display label.', examples: ['Vacation'] }),
  'icon?': type('string').configure({ description: 'Updated Material Icons identifier.', examples: ['beach_access'] }),
  'color?': type('string').configure({ description: 'Updated CSS color (hex recommended).', examples: ['#0ea5e9'] }),
  'category?': type(categoryLiteral).configure({
    description: "Updated category: 'holiday', 'absence', 'location', or 'custom'. Ignored for system types.",
  }),
  'behaviorFlags?': type('unknown').configure({
    description: 'Updated behaviour flags. Ignored for system types.',
  }),
  'config?': type('unknown').configure({
    description: 'Updated per-type configuration. Ignored for system types.',
  }),
  'data?': slotTypeDataSchema,
  'priority?': type('number').configure({ description: 'Updated sort priority.', examples: [10] }),
  'isActive?': type('boolean').configure({
    description: 'Activate (`true`) or deactivate (`false`) this definition.',
  }),
  'translations?': translatableTextSchema(['label']),
}).configure({
  description: 'Partial update for a slot type definition. System types only honour label, icon, color, priority, and isActive.',
});
export type UpdateSlotTypeDefinition = typeof updateSlotTypeDefinitionSchema.t;
